import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Check, ChevronDown } from 'lucide-react';

const STORAGE_KEY = 'restropulse:outlet-filter';

/**
 * Compact scope filter — two dropdowns:
 *   [ Portfolio ▼ ]   [ All Outlets ▼ ]
 *
 * Selection is persisted in localStorage across sessions.
 *
 * Props:
 *   portfolioId, outletIds, onChange({ portfolioId, outletIds })
 */
export function OutletFilter({ portfolioId, outletIds, onChange }) {
  const { getHeaders, API } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [outletsOpen, setOutletsOpen] = useState(false);

  // Hydrate persisted selection on first mount
  useEffect(() => {
    if (portfolioId || (outletIds && outletIds.length)) return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && (saved.portfolioId || (saved.outletIds && saved.outletIds.length))) {
        onChange({
          portfolioId: saved.portfolioId || '',
          outletIds: Array.isArray(saved.outletIds) ? saved.outletIds : [],
        });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        portfolioId: portfolioId || '',
        outletIds: outletIds || [],
      }));
    } catch { /* ignore quota */ }
  }, [portfolioId, outletIds]);

  useEffect(() => {
    axios.get(`${API}/admin/portfolios`, { headers: getHeaders() })
      .then(res => setPortfolios(res.data || []))
      .catch(() => setPortfolios([]));
    axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
      .then(res => setOutlets((res.data || []).filter(o => o.status === 'active')))
      .catch(() => setOutlets([]));
  }, [API, getHeaders]);

  // ── Reconcile persisted selection against the live server response ──
  //   iter-379.2C — Fixes the "6 outlets → all zeros" regression seen when
  //   a browser localStorage carries outlet_ids from a previous tenant.
  //   Any persisted id that does not exist in the fetched `outlets`, or a
  //   portfolio that no longer exists, is silently dropped. If everything
  //   was stale we reset to "All Outlets" scope so requests succeed.
  useEffect(() => {
    if (!outlets.length && !portfolios.length) return;
    const outletIdSet = new Set(outlets.map(o => o.id));
    const portfolioIdSet = new Set(portfolios.map(p => p.id));
    const cleanedOutletIds = (outletIds || []).filter(id => outletIdSet.has(id));
    const cleanedPortfolioId = portfolioId && portfolioIdSet.has(portfolioId)
      ? portfolioId : '';
    const changed =
      cleanedOutletIds.length !== (outletIds || []).length ||
      cleanedPortfolioId !== (portfolioId || '');
    if (changed) {
      onChange({ portfolioId: cleanedPortfolioId, outletIds: cleanedOutletIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets, portfolios]);

  // Outlets visible under the chosen portfolio (or all if no portfolio)
  const scopedOutlets = useMemo(
    () => portfolioId ? outlets.filter(o => o.portfolio_id === portfolioId) : outlets,
    [outlets, portfolioId]
  );

  const handlePortfolioChange = (v) => {
    const id = v === 'all' ? '' : v;
    // Reset outlet selection on portfolio change so we don't keep stale outlet ids
    onChange({ portfolioId: id, outletIds: [] });
    window.dispatchEvent(new CustomEvent('portfolio-changed', {
      detail: portfolios.find(p => p.id === id) || null
    }));
  };

  const toggleOutlet = (id) => {
    const next = (outletIds || []).includes(id)
      ? outletIds.filter(x => x !== id)
      : [...(outletIds || []), id];
    onChange({ portfolioId, outletIds: next });
  };

  const selectedCount = (outletIds || []).length;
  const outletsLabel = selectedCount === 0
    ? 'All Outlets'
    : selectedCount === 1
      ? (outlets.find(o => o.id === outletIds[0])?.name || '1 outlet')
      : `${selectedCount} outlets`;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="outlet-filter">
      {portfolios.length > 0 && (
        <Select value={portfolioId || 'all'} onValueChange={handlePortfolioChange}>
          <SelectTrigger className="h-8 text-xs border-[#E2E8DF] w-[160px]" data-testid="portfolio-select-trigger">
            <SelectValue placeholder="Portfolio" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="portfolio-option-all">All Portfolios</SelectItem>
            {portfolios.map(p => (
              <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-${p.id}`}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Popover open={outletsOpen} onOpenChange={setOutletsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm"
            className="h-8 text-xs border-[#E2E8DF] gap-1.5 font-normal min-w-[150px] justify-between"
            data-testid="outlets-select-trigger">
            <span className="truncate">{outletsLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[260px] p-0" data-testid="outlets-popover">
          <div className="max-h-72 overflow-auto py-1">
            {scopedOutlets.length === 0 && (
              <p className="px-3 py-2 text-xs text-[#5C6B64]">No active outlets</p>
            )}
            {scopedOutlets.map(o => {
              const checked = (outletIds || []).includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggleOutlet(o.id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-[#F3F5F2] ${checked ? 'bg-[color:var(--color-primary)]/5 font-medium text-[color:var(--color-primary)]' : 'text-[#1A2421]'}`}
                  data-testid={`outlet-option-${o.id}`}>
                  <span className="truncate">{o.name}</span>
                  {checked && <Check className="w-3.5 h-3.5 text-[color:var(--color-primary)] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t border-[#E2E8DF] px-3 py-2 bg-[#F9F9F8]">
            <button
              type="button"
              className="text-[11px] text-[#5C6B64] hover:text-[#1A2421] font-medium"
              onClick={() => onChange({ portfolioId, outletIds: [] })}
              data-testid="outlets-select-all-clear">
              All Outlets
            </button>
            <span className="text-[10px] text-[#9CA3AF]">{selectedCount} selected</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
